import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X, Share, Check } from 'lucide-react';
import chopperSwapLogo from '@/assets/chopperswap-logo-new.png';
import { usePwaEnabled } from '@/hooks/usePwaEnabled';
import { useIsDesktop } from '@/hooks/use-tablet';
import { useToast } from '@/hooks/use-toast';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true;
};

// Check if running in Lovable preview iframe for development visibility
const isPreviewEnvironment = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname.includes('lovable.app') && hostname.includes('preview');
};

export const HomeInstallPrompt = () => {
  const pwaEnabled = usePwaEnabled();
  const isDesktop = useIsDesktop();
  const { toast } = useToast();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setIsInstalled(true);
      return;
    }

    // Check localStorage for previous dismissal (7-day expiry)
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    if (wasDismissed) {
      const dismissedDate = new Date(wasDismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 30) {
        setDismissed(true);
        return;
      }
    }

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Delay visibility for smooth animation
    const timer = setTimeout(() => setIsVisible(true), 300);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    // In preview environment, show a toast explaining
    if (isPreviewEnvironment() && !deferredPrompt && !isIOS()) {
      toast({
        title: "Preview Mode",
        description: "PWA installation works on the published domain. This is a design preview.",
      });
      return;
    }

    if (isIOS()) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDismissed(true);
        setDeferredPrompt(null);
      }
    } catch (error) {
      console.error('Install prompt error:', error);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  // Don't show if PWA is disabled, already installed, or dismissed
  if (!pwaEnabled || isInstalled || dismissed) return null;

  // Check if install is possible (real event, iOS, or preview for testing)
  const canInstall = !!deferredPrompt || isIOS() || isPreviewEnvironment();
  
  // Don't render if can't install and not in preview
  if (!canInstall) return null;

  // Don't show until visible (for animation timing)
  if (!isVisible) return null;

  const benefits = [
    'Instant notifications',
    'Offline access',
    'Quick home screen access'
  ];

  // Only render on mobile/tablet
  if (isDesktop) return null;

  return (
    <section className="px-4 py-6 bg-gradient-to-b from-primary/5 to-transparent">
      <div className="max-w-md mx-auto">
        <div className="glass-card p-6 relative animate-in fade-in duration-500">
          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-muted-foreground/50 hover:text-foreground transition-colors p-1.5 rounded-full hover:bg-muted/30 z-20"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>

          {/* iOS Instructions Overlay */}
          {showIOSInstructions && (
            <div className="absolute inset-0 bg-background/98 backdrop-blur-xl rounded-2xl p-6 z-10 flex flex-col">
              <button
                onClick={() => setShowIOSInstructions(false)}
                className="absolute top-4 right-4 text-muted-foreground/50 hover:text-foreground transition-colors p-1.5 rounded-full hover:bg-muted/30"
              >
                <X className="h-4 w-4" />
              </button>
              <h4 className="font-semibold text-foreground text-lg mb-4">Install on iOS</h4>
              <ol className="text-sm text-muted-foreground space-y-3 flex-1">
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold flex-shrink-0">1</span>
                  <span>Tap the <Share className="inline h-4 w-4 mx-1 text-primary" /> Share button in Safari</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold flex-shrink-0">2</span>
                  <span>Scroll down and tap "Add to Home Screen"</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold flex-shrink-0">3</span>
                  <span>Tap "Add" to install ChopperSwap</span>
                </li>
              </ol>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowIOSInstructions(false)}
                className="mt-4"
              >
                Got it
              </Button>
            </div>
          )}

          {/* Main content */}
          <div className="flex items-center gap-5 pr-6">
            {/* Phone mockup */}
            <div className="flex-shrink-0">
              <div className="relative w-[72px] h-[140px] bg-slate-900 rounded-[20px] p-[3px] shadow-xl border border-slate-700/50">
                <div className="absolute top-[6px] left-1/2 -translate-x-1/2 w-8 h-[5px] bg-slate-800 rounded-full" />
                <div className="w-full h-full bg-gradient-to-br from-primary/10 via-background to-blue-500/10 rounded-[17px] flex items-center justify-center overflow-hidden">
                  <img 
                    src={chopperSwapLogo} 
                    alt="ChopperSwap" 
                    className="w-12 h-12 object-contain drop-shadow-lg"
                  />
                </div>
                <div className="absolute bottom-[4px] left-1/2 -translate-x-1/2 w-10 h-[3px] bg-slate-600 rounded-full" />
              </div>
            </div>

            {/* Text content */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground text-lg leading-tight">
                Get ChopperSwap
              </h3>
              <p className="text-muted-foreground text-sm mt-0.5">
                on Your Device
              </p>
              
              {/* Benefits list */}
              <ul className="mt-3 space-y-1.5">
                {['Instant push notifications', 'Works offline anywhere', 'Quick access from home screen'].map((benefit, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Install button */}
          <Button
            onClick={handleInstall}
            className="w-full mt-5 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
          >
            <Download className="h-4 w-4 mr-2" />
            Install App
          </Button>
        </div>
      </div>
    </section>
  );
};
